'use client';
import { useMemo } from 'react';

import { useRouter } from 'next/navigation';
import { useTranslations } from 'next-intl';
import { toast } from 'sonner';
import { type Address } from 'viem';
import { useAccount } from 'wagmi';

import SwapIcon from '@/assets/icons/swap.svg';
import { P } from '@/components/shared/Typography';
import { Button } from '@/components/ui/button';
import { usePoolsTokenBalances } from '@/hooks/PoolsToken/usePoolsTokenBalances';
import { usePoolsTokenContractCalls } from '@/hooks/PoolsToken/usePoolsTokenContractCalls';
import { usePoolsTokenSwap } from '@/hooks/PoolsToken/usePoolsTokenSwap';
import { usePersonaKYC } from '@/hooks/usePersonaKYC';
import { useTokenImport } from '@/hooks/useTokenImport';
import { useWalletConnection } from '@/hooks/useWalletConnection';
import { calculatePoolMetrics } from '@/lib/utils/PoolsToken/calculatePoolMetrics';
import { getTokenIcons } from '@/lib/utils/PoolsToken/getTokenIcons';
import { validateBalance } from '@/lib/utils/PoolsToken/validateBalance';
import { removeCommas } from '@/lib/utils/removeCommas';
import { useGlobalData } from '@/providers/GlobalDataProvider';

import { ExecuteSwapButton } from './components/ExecuteSwapButton';
import { PoolProgress } from './components/PoolProgress';
import { PoolStats } from './components/PoolStats';
import { SwapInput } from './components/SwapInput';
import { SwapOutput } from './components/SwapOutput';

import type { T_Pool } from '@/ts/types/T_Pool';

interface I_PoolsTokenProps {
  pool: T_Pool;
}

const PoolsToken = ({ pool }: I_PoolsTokenProps) => {
  const t = useTranslations('pools');
  const tPredictions = useTranslations('predictions');
  const router = useRouter();
  const { isConnected } = useAccount();
  const { openWalletConnect } = useWalletConnection();
  const user = useGlobalData('user');
  const { needsKYC } = usePersonaKYC();

  // A user is suspended if they are explicitly suspended OR if they need KYC
  const isSuspended = !!user?.suspended || needsKYC;

  const poolContractAddress = pool.poolAddress as Address;
  const tokenAddress = pool.tokenAddress as Address;
  const tokenDecimals = pool.decimals;

  const { importTokenToWallet, isImporting: isImportingToken } = useTokenImport(
    {
      tokenAddress,
      tokenSymbol: pool.tokenSymbol,
      fallbackDecimals: tokenDecimals
    }
  );

  const handleImportToken = async () => {
    // Check if mobile (basic check)
    const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;

    console.log('Token Button Clicked', {
      isMobile,
      width: typeof window !== 'undefined' ? window.innerWidth : 'server'
    });

    if (isMobile) {
      if (tokenAddress) {
        navigator.clipboard.writeText(tokenAddress);
        toast.success(t('addressCopied') || 'Address copied');
      }
      return;
    }

    // Desktop: Import Token
    console.log('Attempting desktop import...');
    try {
      const result = await importTokenToWallet();
      console.log('Import result:', result);
    } catch (e) {
      console.error('Import failed explicitly:', e);
    }
  };

  const {
    usdcBalance,
    tokenBalance,
    actualTokenDecimals,
    formattedUsdcBalance,
    formattedTokenBalance,
    refetchUsdcBalance,
    refetchTokenBalance
  } = usePoolsTokenBalances({
    poolContractAddress,
    tokenAddress,
    tokenDecimals
  });

  const swapLogic = usePoolsTokenSwap({
    pool,
    poolContractAddress,
    tokenAddress,
    tokenDecimals,
    refetchBalances: async () => {
      const results = await Promise.all([
        refetchUsdcBalance(),
        refetchTokenBalance()
      ]);
      router.refresh();
      return results;
    },
    t,
    isConnected,
    isSuspended,
    openWalletConnect
  });

  const {
    swapDirection,
    inputValue,
    validatedInput,
    inputAmountBigInt,
    isInputFocused,
    isInputPendingCalculation,
    isUpdatingInputFromCalculation,
    isFullyConnected,
    isBusy,
    buyState,
    sellState,
    activeChainId,
    isTokensFinished,
    handleSwapDirection,
    handleSwap,
    handleInputChange,
    handleFocusOut,
    setIsInputFocused,
    setIsInputPendingCalculation,
    setIsUpdatingInputFromCalculation,
    setInputValue
  } = swapLogic;

  const { outputValue, feeValue } = usePoolsTokenContractCalls({
    poolContractAddress,
    swapDirection,
    inputAmountBigInt,
    tokenDecimals,
    validatedInput,
    isInputFocused,
    isInputPendingCalculation,
    isUpdatingInputFromCalculation,
    activeChainId,
    setIsInputPendingCalculation,
    setIsUpdatingInputFromCalculation
  });

  const hasInsufficientBalance = useMemo(
    () =>
      validateBalance({
        isFullyConnected,
        inputAmountBigInt,
        swapDirection,
        usdcBalance,
        tokenBalance,
        actualTokenDecimals,
        tokenDecimals
      }),
    [
      isFullyConnected,
      inputAmountBigInt,
      swapDirection,
      usdcBalance,
      tokenBalance,
      actualTokenDecimals,
      tokenDecimals
    ]
  );

  const { totalPoolTokens, availableTokensPercentage, availableTokensCount } =
    useMemo(() => calculatePoolMetrics(pool), [pool]);

  const { inputIcon, inputLabel, outputIcon, outputLabel } = useMemo(
    () => getTokenIcons(pool, swapDirection),
    [pool, swapDirection]
  );

  const inputBalance =
    swapDirection === 'usdc-to-token'
      ? formattedUsdcBalance
      : formattedTokenBalance;
  const outputBalance =
    swapDirection === 'usdc-to-token'
      ? formattedTokenBalance
      : formattedUsdcBalance;

  const insufficientBalanceMessage = useMemo(() => {
    if (!hasInsufficientBalance) return undefined;
    return swapDirection === 'usdc-to-token'
      ? t('insufficientBalance')
      : `Insufficient ${pool.tokenSymbol} balance`;
  }, [hasInsufficientBalance, swapDirection, pool.tokenSymbol, t]);

  const isTokenNotImported = useMemo(() => {
    return (
      swapDirection === 'token-to-usdc' &&
      isFullyConnected &&
      tokenBalance === undefined
    );
  }, [swapDirection, isFullyConnected, tokenBalance]);

  const isTokenBalanceZeroOrUndefined = useMemo(() => {
    return (
      swapDirection === 'token-to-usdc' &&
      isFullyConnected &&
      (tokenBalance === undefined ||
        tokenBalance === 0n ||
        tokenBalance === BigInt(0))
    );
  }, [swapDirection, isFullyConnected, tokenBalance]);

  const hasInputValue = useMemo(() => {
    if (!validatedInput) return false;
    const cleaned = removeCommas(validatedInput);
    return cleaned && parseFloat(cleaned) > 0;
  }, [validatedInput]);

  // Set placeholder based on swap direction
  const inputPlaceholder = useMemo(() => {
    return swapDirection === 'usdc-to-token' ? '0.000000' : '0.000000';
  }, [swapDirection]);

  const outputPlaceholder = useMemo(() => {
    return swapDirection === 'usdc-to-token' ? '0.000000' : '0.000000';
  }, [swapDirection]);

  // Calculate available tokens logic
  const isExceedingLiquidity = useMemo(() => {
    if (swapDirection !== 'usdc-to-token' || !outputValue) return false;

    const totalSupply = parseFloat(pool.totalSupply || '0');
    const totalSoldTokens = parseFloat(
      pool.blockchainData?.totalSoldTokensDecimal || '0'
    );
    const available = totalSupply - totalSoldTokens;

    // Output value is the estimated tokens we will receive
    const amountToReceive = parseFloat(removeCommas(outputValue));

    return amountToReceive > available;
  }, [swapDirection, outputValue, pool]);

  const isButtonDisabled =
    (isSuspended && swapDirection === 'usdc-to-token') ||
    isBusy ||
    !validatedInput ||
    parseFloat(removeCommas(validatedInput)) <= 0 ||
    hasInsufficientBalance ||
    isTokenNotImported ||
    (isTokenBalanceZeroOrUndefined && hasInputValue) ||
    (swapDirection === 'usdc-to-token' && isTokensFinished) ||
    isExceedingLiquidity;

  return (
    <div className="grid gap-5">
      <div className="relative grid gap-4">
        <SwapInput
          value={inputValue}
          onChange={value => {
            handleInputChange(value);
          }}
          onFocus={() => {
            setIsInputFocused(true);
            setIsInputPendingCalculation(true);
          }}
          onBlur={() => {
            setIsInputFocused(false);
            handleFocusOut();
          }}
          balance={inputBalance}
          tokenIcon={inputIcon}
          tokenLabel={inputLabel}
          placeholder={inputPlaceholder}
          hasError={hasInsufficientBalance}
          disabled={
            (isSuspended && swapDirection === 'usdc-to-token') ||
            isBusy ||
            (swapDirection === 'usdc-to-token' && isTokensFinished)
          }
          showBalance={isFullyConnected}
          onTokenButtonClick={
            inputLabel === pool.tokenSymbol ? handleImportToken : undefined
          }
          isImporting={
            inputLabel === pool.tokenSymbol ? isImportingToken : false
          }
        />

        {!isSuspended && (
          <Button
            variant="default"
            icon={<SwapIcon />}
            size="icon"
            onClick={handleSwapDirection}
            disabled={
              isBusy || (swapDirection === 'token-to-usdc' && isTokensFinished)
            }
            className="absolute left-1/2 top-1/2 z-10 -translate-y-1/2 -translate-x-1/2 flex items-center p-4 w-fit rounded-full bg-primary hover:bg-primary/90"
          />
        )}

        <SwapOutput
          value={outputValue}
          balance={outputBalance}
          tokenIcon={outputIcon}
          tokenLabel={outputLabel}
          placeholder={outputPlaceholder}
          showBalance={isFullyConnected}
          onTokenButtonClick={
            outputLabel === pool.tokenSymbol ? handleImportToken : undefined
          }
          isImporting={
            outputLabel === pool.tokenSymbol ? isImportingToken : false
          }
          disabled={
            (isSuspended && swapDirection === 'usdc-to-token') || isBusy
          }
        />
      </div>
      {isFullyConnected && (
        <div className="flex justify-center items-center gap-1">
          <P className="text-sm font-semibold">{t('swapFee')}:</P>
          <P className="text-sm font-semibold">{feeValue} USDC</P>
        </div>
      )}
      {hasInsufficientBalance &&
        isFullyConnected &&
        insufficientBalanceMessage && (
          <P className="text-destructive text-sm font-medium text-center">
            {insufficientBalanceMessage}
          </P>
        )}

      {isExceedingLiquidity && swapDirection === 'usdc-to-token' && (
        <P className="text-destructive text-sm font-medium text-center">
          {t('insufficientLiquidity') || 'Insufficient liquidity in the pool'}
        </P>
      )}

      {isTokensFinished && swapDirection === 'usdc-to-token' && (
        <P className="text-muted-foreground text-sm font-medium text-center">
          {t('tokensSoldOut')}
        </P>
      )}

      {/*<PoolsTokenSelect />*/}

      <PoolStats
        totalPoolVolume={pool.blockchainData.currentUsdcBalanceDecimal || '-'}
        totalPoolTokens={totalPoolTokens}
      />

      <PoolProgress
        percentage={availableTokensPercentage}
        availableTokensCount={availableTokensCount}
      />

      <ExecuteSwapButton
        onClick={handleSwap}
        disabled={isButtonDisabled}
        isFullyConnected={isFullyConnected}
        isBusy={isBusy}
        swapDirection={swapDirection}
        buyState={buyState}
        sellState={sellState}
        t={t}
        openWalletConnect={openWalletConnect}
      />
    </div>
  );
};

export default PoolsToken;
